// src/pages/VendorPage.js — FIXED
// Fix 1: Payment config toggle per method
// Fix 2: Add/manage custom categories
// Fix 3: Image URL input for menu items
import React, { useState, useEffect } from 'react';
import { getMenu, createMenuItem, toggleMenuItem, deleteMenuItem, updateMenuItem } from '../services/api';
import { useAuth } from '../context/AuthContext';
import POSConnect from '../components/POSConnect';
import StockAlerts from '../components/StockAlerts';
import api from '../services/api';
import MenuImportWizard from '../components/MenuImportWizard';
import BundleBuilder from '../components/BundleBuilder';
import TemplatesTab from '../components/TemplatesTab';
import ReportsTab from '../components/ReportsTab';
import usePushNotifications from '../hooks/usePushNotifications';
import {
  TAB_VISIBILITY, DEPLOYMENT_MODE_LABELS,
  DEPLOYMENT_MODE_DESCRIPTIONS, DEPLOYMENT_MODES,
} from '../constants/deploymentModes';
import { calculateServiceFee } from '../utils/serviceFeeCalculator';
import { parseDietary, DIETARY_TAGS, ALLERGEN_TAGS } from '../utils/dietary';

const MODE_COLOURS = {
  STANDALONE_FULL:       { bg:'#1a2d3d', border:'#185FA5', text:'#378ADD' },
  STANDALONE_POS_BRIDGE: { bg:'#1a3d2a', border:'#3B6D11', text:'#639922' },
  MALL_MANAGED:          { bg:'#3d2e10', border:'#854F0B', text:'#EF9F27' },
  MALL_PLUS_STALL:       { bg:'#2a1a3d', border:'#534AB7', text:'#7F77DD' },
  TABLE_QR_ONLY:         { bg:'#1a3d3d', border:'#0F6E56', text:'#1D9E75' },
};

const DEFAULT_CATS = ['Wraps','Burgers','Pizza','Rice','Sides','Drinks','Desserts','Sandwiches'];
const THEME_PRESETS = [
  { id:'dark',  label:'Dark',  bg:'#0f0f0f',  accent:'#f5a623' },
  { id:'light', label:'Light', bg:'#F8F9FA',  accent:'#E8A33D' },
  { id:'brand', label:'Brand', bg:'#0A1628',  accent:null },
];
// Literal (non-CSS-var) colors for the scoped kiosk mock preview — must NOT touch document-level theme
const THEME_COLORS = {
  dark:  { bg:'#0f0f0f', bg2:'#1a1a1a', bg3:'#242424', text:'#f0ede8', text2:'#a09890', accent:'#f5a623' },
  light: { bg:'#F8F9FA', bg2:'#FFFFFF', bg3:'#F0F2F5', text:'#1F2D3D', text2:'#4A5568', accent:'#E8A33D' },
  brand: { bg:'#0A1628', bg2:'#0F1F38', bg3:'#13243F', text:'#FFFFFF', text2:'#8FA3BF', accent:'#00A886' },
};
const EMPTY_FORM   = { name:'', price:'', category:'', emoji:'🍽', description:'', imageUrl:'', itemType:'SIMPLE', components:[], dietary:[], allergens:[] };

// Multi-select checkbox group for dietary tags / allergens. Tags not in `options`
// (e.g. from an import) are left untouched when the vendor toggles others.
function TagPicker({ options, value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter(v => v !== k) : [...value, k]);
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 16px' }}>
      {options.map(([k, label]) => (
        <label key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={value.includes(k)} onChange={() => toggle(k)} style={{ width:'auto' }} />
          {label}
        </label>
      ))}
    </div>
  );
}
const ALL_PAYMENT_METHODS = [
  { id:'CASH',   icon:'💵', label:'Cash',        desc:'Manual cash collection'   },
  { id:'CARD',   icon:'💳', label:'Card',        desc:'Visa / Mastercard / Amex' },
  { id:'UPI',    icon:'📱', label:'UPI / QR',    desc:'Scan & pay via phone'     },
  { id:'WALLET', icon:'⚡', label:'Wallet',      desc:'Apple Pay / Google Pay'   },
  { id:'TAP',    icon:'🔵', label:'Tap Payments',desc:'UAE fast payment gateway' },
];

export default function VendorPage() {
  const { vendor, updateUser } = useAuth();
  const [menu,       setMenu]       = useState([]);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [adding,     setAdding]     = useState(false);
  const [activeTab,    setActiveTab]  = useState('menu');
  const [showImporter, setShowImporter] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATS);
  const [newCat,     setNewCat]     = useState('');
  const [payConfig,  setPayConfig]  = useState({ CASH:true, CARD:true, UPI:true, WALLET:false, TAP:false, allowCashOnQR:false });
  const [payMsg,     setPayMsg]     = useState('');
  const [editItem,   setEditItem]   = useState(null);
  const [tableCount,       setTableCount]       = useState(10);
  const [franchiseInfo,    setFranchiseInfo]    = useState(null);
  const [franchiseSyncing, setFranchiseSyncing] = useState(false);
  const [franchiseMsg,     setFranchiseMsg]     = useState('');

  // Deployment mode
  const deploymentMode = vendor?.deploymentMode || DEPLOYMENT_MODES.STANDALONE_FULL;
  const tabVis         = TAB_VISIBILITY[deploymentMode] || TAB_VISIBILITY.STANDALONE_FULL;
  const colours        = MODE_COLOURS[deploymentMode]   || MODE_COLOURS.STANDALONE_FULL;

  // Vendor settings state — initialised from vendor object
  const [settingsForm, setSettingsForm] = useState({
    name:        vendor?.name        || '',
    emoji:       vendor?.emoji       || '🍽',
    description: vendor?.description || '',
  });
  const [settingsMsg,    setSettingsMsg]    = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [notifPrefs,     setNotifPrefs]     = useState({ smsEnabled: false, pushEnabled: false });
  const [notifMsg,       setNotifMsg]       = useState('');
  const push = usePushNotifications();

  // Kiosk appearance — initialised from vendor object
  const [kioskTheme,  setKioskTheme]  = useState(vendor?.kioskTheme || 'dark');
  const [brandColor,  setBrandColor]  = useState(vendor?.brandColor || '#00A886');
  const [bgColor,     setBgColor]     = useState(vendor?.bgColor || '#0A1628');
  const [tileColor,   setTileColor]   = useState(vendor?.tileColor || '#0F1F38');
  const [fontColor,   setFontColor]   = useState(vendor?.fontColor || '#FFFFFF');
  const [buttonTextColor, setButtonTextColor] = useState(vendor?.buttonTextColor || '#000000');
  const [surfaceColor, setSurfaceColor] = useState(vendor?.surfaceColor || '#13243F');
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMsg,    setThemeMsg]    = useState('');

  // Payment collection mode — initialised from vendor object
  const [collectionMode,   setCollectionMode]   = useState(vendor?.collectionMode || 'PLATFORM_DEFAULT');
  const [paymentAccountId, setPaymentAccountId] = useState(vendor?.paymentAccountId || '');
  const [paymentProvider,  setPaymentProvider]  = useState(vendor?.paymentProvider || 'TAP');
  const [collectionSaving, setCollectionSaving] = useState(false);
  const [collectionMsg,    setCollectionMsg]    = useState('');

  // Service fee config state
  const initFeeConfig = () => {
    const cfg = vendor?.serviceFeeConfig;
    if (!cfg) return { enabled: false, type: 'FIXED', fixedAmount: 0, percentage: 0, maxCap: 0, minOrderAmount: 0, label: 'Service Fee', platformSharePercent: 100, mallSharePercent: 0 };
    const parsed = typeof cfg === 'string' ? (() => { try { return JSON.parse(cfg); } catch(_) { return {}; } })() : cfg;
    return { enabled: false, type: 'FIXED', fixedAmount: 0, percentage: 0, maxCap: 0, minOrderAmount: 0, label: 'Service Fee', platformSharePercent: 100, mallSharePercent: 0, ...parsed };
  };
  const [feeConfig,    setFeeConfig]    = useState(initFeeConfig);
  const [feeSaving,    setFeeSaving]    = useState(false);
  const [feeMsg,       setFeeMsg]       = useState('');
  const [feePreviewAmt, setFeePreviewAmt] = useState(50);

  const saveFeeConfig = async () => {
    setFeeSaving(true);
    try {
      await api.patch('/vendors/me', { serviceFeeConfig: feeConfig });
      updateUser({ serviceFeeConfig: feeConfig });
      setFeeMsg('✅ Saved');
    } catch (err) {
      setFeeMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setFeeSaving(false);
      setTimeout(() => setFeeMsg(''), 3000);
    }
  };

  const saveCollectionMode = async () => {
    setCollectionSaving(true);
    try {
      const r = await api.patch('/vendors/me', { collectionMode, paymentAccountId: paymentAccountId || null, paymentProvider });
      updateUser({ collectionMode: r.data.collectionMode, paymentAccountId: r.data.paymentAccountId, paymentProvider: r.data.paymentProvider });
      setCollectionMsg('✅ Saved');
    } catch (err) {
      setCollectionMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setCollectionSaving(false);
      setTimeout(() => setCollectionMsg(''), 3000);
    }
  };

  const saveTheme = async () => {
    if (brandColor && !/^#[0-9A-Fa-f]{6}$/.test(brandColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (bgColor && !/^#[0-9A-Fa-f]{6}$/.test(bgColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (tileColor && !/^#[0-9A-Fa-f]{6}$/.test(tileColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (fontColor && !/^#[0-9A-Fa-f]{6}$/.test(fontColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (buttonTextColor && !/^#[0-9A-Fa-f]{6}$/.test(buttonTextColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (surfaceColor && !/^#[0-9A-Fa-f]{6}$/.test(surfaceColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    setThemeSaving(true);
    try {
      const r = await api.patch('/vendors/me', { kioskTheme, brandColor, bgColor, tileColor, fontColor, buttonTextColor, surfaceColor });
      updateUser({ kioskTheme: r.data.kioskTheme, brandColor: r.data.brandColor, bgColor: r.data.bgColor, tileColor: r.data.tileColor, fontColor: r.data.fontColor, buttonTextColor: r.data.buttonTextColor, surfaceColor: r.data.surfaceColor });
      setThemeMsg('✅ Saved — open your kiosk link to see it live');
    } catch (err) {
      setThemeMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setThemeSaving(false);
      setTimeout(() => setThemeMsg(''), 4000);
    }
  };

  const loadMenu = () => {
    getMenu({ vendorId: vendor?.id })
      .then((r) => setMenu(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    loadMenu();
    // Load payment config
    api.get('/payment-config')
      .then((r) => setPayConfig(r.data))
      .catch(() => {});
    // Load custom categories
    api.get(`/vendors/${vendor?.id}`)
      .then((r) => {
        if (r.data?.categories) {
          try { setCategories(JSON.parse(r.data.categories)); } catch (_) {}
        }
      })
      .catch(() => {});
    // Load franchise info (hub/branch status)
    if (vendor?.id) {
      api.get(`/franchise/status/${vendor.id}`)
        .then(r => setFranchiseInfo(r.data))
        .catch(() => {});
      // Load notification prefs
      api.get('/vendors/me/notification-prefs')
        .then(r => setNotifPrefs(r.data || {}))
        .catch(() => {});
    }
  }, [vendor]); // eslint-disable-line

  // ── Category management ───────────────────────────────────
  const handleAddCategory = async () => {
    if (!newCat.trim()) return;
    const updated = [...categories, newCat.trim()];
    setCategories(updated);
    setNewCat('');
    await api.patch(`/vendors/${vendor?.id}`, { categories: JSON.stringify(updated) }).catch(() => {});
  };

  const handleRemoveCategory = async (cat) => {
    const updated = categories.filter((c) => c !== cat);
    setCategories(updated);
    await api.patch(`/vendors/${vendor?.id}`, { categories: JSON.stringify(updated) }).catch(() => {});
  };

  // ── Payment config ────────────────────────────────────────
  const handleTogglePayment = async (method) => {
    const updated = { ...payConfig, [method]: !payConfig[method] };
    setPayConfig(updated);
    try {
      await api.patch('/payment-config', updated);
      setPayMsg('✅ Saved');
    } catch (_) { setPayMsg('❌ Save failed'); }
    setTimeout(() => setPayMsg(''), 2000);
  };

  // ── Menu CRUD ─────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.name || !form.price) return;
    const cat  = form.category || categories[0] || 'General';
    const data = { ...form, price: Number(form.price), category: cat, stock: 100 };
    try {
      const res = await createMenuItem(data);
      setMenu((m) => [...m, res.data]);
    } catch (_) {
      setMenu((m) => [...m, { ...data, id: Date.now().toString(), isActive: true, posSource:'manual' }]);
    }
    setForm(EMPTY_FORM);
    setAdding(false);
  };

  const isBundleForm = form.itemType === 'BUNDLE';

  const handleUpdate = async () => {
    if (!editItem) return;
    const payload = {
      name: form.name, price: Number(form.price), description: form.description,
      emoji: form.emoji, imageUrl: form.imageUrl, category: form.category,
      itemType: form.itemType,
      dietary: form.dietary, allergens: form.allergens,
      ...(form.itemType === 'BUNDLE' ? { components: form.components } : {}),
    };
    try {
      const res = await updateMenuItem(editItem.id, payload);
      setMenu((m) => m.map((i) => i.id === editItem.id ? res.data : i));
    } catch (_) {
      setMenu((m) => m.map((i) => i.id === editItem.id ? { ...i, ...form, price: Number(form.price) } : i));
    }
    setEditItem(null);
    setForm(EMPTY_FORM);
    setAdding(false);
  };

  const handleToggle  = async (id) => { try { await toggleMenuItem(id); } catch (_) {} setMenu((m) => m.map((i) => i.id === id ? { ...i, isActive: !i.isActive } : i)); };
  const handleDelete  = async (id) => { try { await deleteMenuItem(id); } catch (_) {} setMenu((m) => m.filter((i) => i.id !== id)); };
  const handleEdit    = (item) => {
    setEditItem(item);
    setForm({
      name: item.name, price: item.price, category: item.category, emoji: item.emoji,
      description: item.description || '', imageUrl: item.imageUrl || '',
      itemType: item.itemType || 'SIMPLE',
      dietary: parseDietary(item.dietary), allergens: parseDietary(item.allergens),
      components: (item.bundleComponents || []).map(c => ({
        displayName: c.displayName,
        templateId:  c.templateId,
      })),
    });
    setAdding(true);
  };

  const tabStyle = (id) => ({
    padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:500,
    background: activeTab===id ? 'var(--accent)' : 'transparent',
    color:      activeTab===id ? '#000' : 'var(--text2)',
    border:'none', cursor:'pointer',
  });

  return (
    <div className="page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <div className="page-title">Vendor Admin Panel</div>
          <div className="page-sub">Manage menu, categories, payment methods and POS</div>
        </div>
        {activeTab === 'menu' && (
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-ghost btn-sm"
              onClick={() => setShowImporter(true)}
              style={{ fontSize:12 }}>
              📥 Import Menu
            </button>
            <button className="btn btn-primary" onClick={() => { setAdding(!adding); setEditItem(null); setForm(EMPTY_FORM); }}>
              {adding ? 'Cancel' : '+ Add Item'}
            </button>
          </div>
        )}
      </div>

      {/* ── Model 2 onboarding checklist ── */}
      {deploymentMode === DEPLOYMENT_MODES.MALL_MANAGED && (
        <div style={{ background:'#1a2a3a', border:'1px solid #2a4a6a', borderRadius:10, padding:'16px 20px', marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:14, color:'#60b0f0', marginBottom:12 }}>
            🏬 Mall Onboarding Checklist
          </div>
          {[
            {
              done:  menu.length > 0,
              label: 'Add menu items',
              hint:  menu.length > 0 ? `${menu.length} item${menu.length !== 1 ? 's' : ''} added` : 'Go to the Menu tab to add your first item',
            },
            {
              done:  !!vendor?.mallId,
              label: 'Assigned to a mall',
              hint:  vendor?.mallId ? 'Assigned by your admin' : 'Contact your admin to assign you to a mall',
            },
            {
              done:  !!vendor?.isActive,
              label: 'Account active',
              hint:  vendor?.isActive ? 'Your account is live' : 'Contact your admin to activate your account',
            },
          ].map(({ done, label, hint }) => (
            <div key={label} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
              <span style={{ fontSize:16, marginTop:1 }}>{done ? '✅' : '⬜'}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color: done ? 'var(--green)' : 'var(--text)' }}>{label}</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>{hint}</div>
              </div>
            </div>
          ))}
          {menu.length > 0 && vendor?.mallId && vendor?.isActive && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'#1a3d2a', borderRadius:8, fontSize:12, color:'var(--green)', fontWeight:600 }}>
              🎉 All steps complete — you are live in the mall kiosk!
            </div>
          )}
        </div>
      )}

      {/* Deployment mode banner */}
      <div style={{ padding:'9px 14px', borderRadius:8, marginBottom:16,
        background:colours.bg, border:`1px solid ${colours.border}`,
        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
        <div>
          <span style={{ fontWeight:600, fontSize:12, color:colours.text }}>
            {DEPLOYMENT_MODE_LABELS[deploymentMode]}
          </span>
          <span style={{ fontSize:11, color:'var(--text2)', marginLeft:10 }}>
            {DEPLOYMENT_MODE_DESCRIPTIONS[deploymentMode]}
          </span>
        </div>
      </div>

      {/* Tabs — gated by deployment mode */}
      <div style={{ display:'flex', gap:4, marginBottom:24, background:'var(--bg2)', borderRadius:10, padding:4, width:'fit-content', flexWrap:'wrap' }}>
        {[
          ['menu',       '🍽 Menu',               true],
          ['templates',  '🗂 Templates',           true],
          ['categories', '📂 Categories',          true],
          ['payments',   '💳 Payment Methods',     true],
          ['stock',      '📦 Stock Alerts',        true],
          ['reports',    '📊 Reports',             true],
          ['pos',        '🔗 POS Integration',     tabVis.pos],
          ['qr',         '📱 Table QRs',           deploymentMode === DEPLOYMENT_MODES.TABLE_QR_ONLY],
          ['settings',   '⚙️ Settings',            true],
        ].filter(([,, visible]) => visible !== false)
         .map(([id, label]) => (
          <button key={id} style={tabStyle(id)} onClick={() => setActiveTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── POS Tab ── */}
      {activeTab === 'pos' && <POSConnect />}

      {/* ── Templates Tab ── */}
      {activeTab === 'templates' && <TemplatesTab menu={menu} />}

      {/* ── Reports Tab ── */}
      {activeTab === 'reports' && <ReportsTab />}

      {/* ── Table QR Tab (Model 3b only) ── */}
      {activeTab === 'qr' && (
        <div className="card">
          <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>📱 Table QR Codes</div>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
            Print and place these QR codes on each table. Customers scan to order directly from their phone.
          </div>

          {/* Table count picker */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
            <span style={{ fontSize:13, color:'var(--text2)' }}>Number of tables:</span>
            <input type="number" min={1} max={50} value={tableCount}
              onChange={e => setTableCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
              style={{ width:70 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>
              🖨 Print All
            </button>
          </div>

          {/* QR grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:16 }}>
            {Array.from({ length: tableCount }, (_, i) => i + 1).map(n => {
              const base = window.location.origin;
              const slug = vendor?.slug || vendor?.id || 'vendor';
              const url  = `${base}/order/vendor/${slug}?table=${n}`;
              const qr   = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}`;
              return (
                <div key={n} style={{ background:'var(--bg3)', borderRadius:10, padding:16, textAlign:'center', border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8, textTransform:'uppercase', letterSpacing:1 }}>
                    Table {n}
                  </div>
                  <img src={qr} alt={`Table ${n} QR`}
                    style={{ width:140, height:140, borderRadius:6, background:'#fff', padding:4 }}
                    onError={e => { e.target.style.display = 'none'; }} />
                  <div style={{ fontSize:10, color:'var(--text3)', marginTop:8, wordBreak:'break-all' }}>
                    {vendor?.name || 'Stall'} · Table {n}
                  </div>
                  <a href={url} target="_blank" rel="noreferrer"
                    style={{ fontSize:10, color:'var(--accent)', display:'block', marginTop:4 }}>
                    Test link ↗
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Settings Tab ── */}
      {activeTab === 'settings' && (
        <div className="card" style={{ maxWidth:480 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:16 }}>⚙️ Vendor Settings</div>
            {settingsMsg && <span style={{ fontSize:13, color:settingsMsg.startsWith('✅')?'var(--green)':'var(--red)' }}>{settingsMsg}</span>}
          </div>
          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Stall name *</div>
            <input type="text"
              value={settingsForm.name}
              onChange={e => setSettingsForm(p => ({...p, name:e.target.value}))} />
          </div>
          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Emoji icon</div>
            <input type="text"
              value={settingsForm.emoji}
              onChange={e => setSettingsForm(p => ({...p, emoji:e.target.value}))}
              style={{ maxWidth:80 }} />
          </div>
          <div style={{ marginBottom:20 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Description</div>
            <textarea
              value={settingsForm.description}
              onChange={e => setSettingsForm(p => ({...p, description:e.target.value}))}
              style={{ height:80, resize:'vertical' }} />
          </div>
          <div style={{ padding:'10px 14px', background:'var(--bg3)', borderRadius:8, marginBottom:16, fontSize:12, color:'var(--text3)' }}>
            <strong style={{ color:'var(--text2)' }}>Deployment mode:</strong>{' '}
            {DEPLOYMENT_MODE_LABELS[deploymentMode]}<br/>
            Contact your admin to change deployment mode.
          </div>

          {/* ── Franchise info ── */}
          {franchiseInfo && (franchiseInfo.isHub || franchiseInfo.isBranch) && (
            <div style={{ padding:'12px 14px', background:'var(--bg3)', borderRadius:8, marginBottom:16, border:'1px solid var(--border)' }}>
              {franchiseInfo.isHub && (
                <>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>
                    ⛓ Chain Hub — {franchiseInfo.branchCount} branch{franchiseInfo.branchCount !== 1 ? 'es' : ''}
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)', marginBottom:10 }}>
                    Last pushed: {franchiseInfo.lastSync ? new Date(franchiseInfo.lastSync).toLocaleString() : 'Never'}
                  </div>
                  <button className="btn btn-primary btn-sm"
                    disabled={franchiseSyncing}
                    onClick={async () => {
                      setFranchiseSyncing(true);
                      try {
                        const r = await api.post(`/franchise/sync/${vendor.id}`);
                        setFranchiseMsg(`✅ Pushed to ${r.data.total} branch${r.data.total !== 1 ? 'es' : ''} (${r.data.synced} items)`);
                        api.get(`/franchise/status/${vendor.id}`).then(r => setFranchiseInfo(r.data)).catch(() => {});
                      } catch (e) {
                        setFranchiseMsg('❌ ' + (e.response?.data?.error || 'Sync failed'));
                      } finally {
                        setFranchiseSyncing(false);
                        setTimeout(() => setFranchiseMsg(''), 4000);
                      }
                    }}>
                    {franchiseSyncing ? '⏳ Syncing...' : '🔄 Push to All Branches'}
                  </button>
                </>
              )}
              {franchiseInfo.isBranch && (
                <>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>
                    🏪 Branch of {franchiseInfo.hubName}
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)', marginBottom:2 }}>
                    Sync mode: <strong>{franchiseInfo.syncMode}</strong>
                    {franchiseInfo.priceOverride && <span style={{ marginLeft:8, color:'var(--accent)' }}>· Local prices kept</span>}
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)', marginBottom:10 }}>
                    Last synced: {franchiseInfo.lastSync ? new Date(franchiseInfo.lastSync).toLocaleString() : 'Never'}
                  </div>
                  <button className="btn btn-ghost btn-sm"
                    disabled={franchiseSyncing}
                    onClick={async () => {
                      setFranchiseSyncing(true);
                      try {
                        const r = await api.post(`/franchise/sync/${franchiseInfo.hubId}/${vendor.id}`);
                        setFranchiseMsg(`✅ Pulled ${r.data.synced} items from hub`);
                        api.get(`/franchise/status/${vendor.id}`).then(r => setFranchiseInfo(r.data)).catch(() => {});
                      } catch (e) {
                        setFranchiseMsg('❌ ' + (e.response?.data?.error || 'Pull failed'));
                      } finally {
                        setFranchiseSyncing(false);
                        setTimeout(() => setFranchiseMsg(''), 4000);
                      }
                    }}>
                    {franchiseSyncing ? '⏳ Pulling...' : '⬇ Pull from Hub'}
                  </button>
                </>
              )}
              {franchiseMsg && (
                <div style={{ marginTop:8, fontSize:12, color: franchiseMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>
                  {franchiseMsg}
                </div>
              )}
            </div>
          )}

          {/* ── Notifications ── */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:20, marginTop:20 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:12 }}>🔔 Notifications</div>

            {/* Browser Push */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>Browser Push Notifications</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>
                  {!push.supported ? 'Not supported in this browser' :
                    push.permission === 'denied' ? '⚠️ Blocked — enable in browser settings' :
                    push.isSubscribed ? '✅ Active — you\'ll get notified of new orders' :
                    'Get notified when a new paid order arrives'}
                </div>
              </div>
              {push.supported && push.permission !== 'denied' && (
                <button
                  className={push.isSubscribed ? 'btn btn-secondary' : 'btn btn-primary'}
                  style={{ whiteSpace:'nowrap', minWidth:100 }}
                  disabled={push.loading}
                  onClick={() => push.isSubscribed ? push.unsubscribe() : push.subscribe()}>
                  {push.loading ? '⏳...' : push.isSubscribed ? '🔕 Disable' : '🔔 Enable'}
                </button>
              )}
            </div>
            {push.error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:8 }}>{push.error}</div>}

            {/* SMS toggle */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>Customer SMS on Order Ready</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>
                  Sends SMS to customer when you mark order READY. Requires Twilio setup. Charged per SMS.
                </div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={!!notifPrefs.smsEnabled}
                  onChange={async (e) => {
                    const updated = { ...notifPrefs, smsEnabled: e.target.checked };
                    setNotifPrefs(updated);
                    try {
                      await api.patch('/vendors/me/notification-prefs', { smsEnabled: e.target.checked });
                      setNotifMsg(e.target.checked ? '✅ SMS enabled' : '✅ SMS disabled');
                    } catch { setNotifMsg('❌ Save failed'); }
                    setTimeout(() => setNotifMsg(''), 2500);
                  }} />
                <span style={{ fontSize:13 }}>{notifPrefs.smsEnabled ? 'On' : 'Off'}</span>
              </label>
            </div>
            {notifMsg && <div style={{ fontSize:12, color: notifMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{notifMsg}</div>}
          </div>

          {/* ── Kiosk Appearance ── */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:20, marginTop:20, marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:15 }}>🎨 Kiosk Appearance</div>
              {themeMsg && <span style={{ fontSize:13, color:themeMsg.startsWith('✅')?'var(--green)':'var(--red)' }}>{themeMsg}</span>}
            </div>
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              {THEME_PRESETS.map(p => {
                const selected = kioskTheme === p.id;
                const swatchAccent = p.id === 'brand' ? (brandColor || '#00A886') : p.accent;
                return (
                  <div key={p.id}
                    onClick={() => setKioskTheme(p.id)}
                    style={{
                      width:160, height:100, borderRadius:10, cursor:'pointer',
                      background: p.bg, border: selected ? '2px solid var(--accent)' : '2px solid var(--border)',
                      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                      gap:8, position:'relative',
                    }}>
                    {selected && <div style={{ position:'absolute', top:6, right:8, color:'var(--accent)', fontSize:14 }}>✓</div>}
                    <div style={{ width:24, height:24, borderRadius:'50%', background:swatchAccent }} />
                    <div style={{ fontSize:13, fontWeight:600, color: p.id === 'light' ? '#1F2D3D' : '#fff' }}>{p.label}</div>
                  </div>
                );
              })}
            </div>

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Brand color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={brandColor || '#00A886'}
                    onChange={e => setBrandColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={brandColor || ''}
                    onChange={e => setBrandColor(e.target.value)}
                    placeholder="#00A886"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for buttons, highlights, and price labels on your kiosk.
                </div>
              </div>
            )}

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Background color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={bgColor || '#0A1628'}
                    onChange={e => setBgColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={bgColor || ''}
                    onChange={e => setBgColor(e.target.value)}
                    placeholder="#0A1628"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for the main page background on your kiosk.
                </div>
              </div>
            )}

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Tiles color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={tileColor || '#0F1F38'}
                    onChange={e => setTileColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={tileColor || ''}
                    onChange={e => setTileColor(e.target.value)}
                    placeholder="#0F1F38"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for menu item tiles and cards on your kiosk.
                </div>
              </div>
            )}

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Font color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={fontColor || '#FFFFFF'}
                    onChange={e => setFontColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={fontColor || ''}
                    onChange={e => setFontColor(e.target.value)}
                    placeholder="#FFFFFF"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for text on your kiosk.
                </div>
              </div>
            )}

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Button text color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={buttonTextColor || '#000000'}
                    onChange={e => setButtonTextColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={buttonTextColor || ''}
                    onChange={e => setButtonTextColor(e.target.value)}
                    placeholder="#000000"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for text on buttons (e.g. add-to-cart). Pick black or white depending on your brand color for contrast.
                </div>
              </div>
            )}

            {kioskTheme === 'brand' && (
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Surface color</div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={surfaceColor || '#13243F'}
                    onChange={e => setSurfaceColor(e.target.value)}
                    style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
                  <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                    value={surfaceColor || ''}
                    onChange={e => setSurfaceColor(e.target.value)}
                    placeholder="#13243F"
                    style={{ maxWidth:140 }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
                  This color is used for the search bar, language button, category tabs, and item image placeholders.
                </div>
              </div>
            )}

            {/* Live mock preview — uses literal colors, never touches the document theme */}
            {(() => {
              const c   = THEME_COLORS[kioskTheme] || THEME_COLORS.dark;
              const ac  = kioskTheme === 'brand' ? (brandColor || c.accent) : c.accent;
              const bg  = kioskTheme === 'brand' ? (bgColor || c.bg) : c.bg;
              const tbg = kioskTheme === 'brand' ? (tileColor || c.bg2) : c.bg2;
              const tc  = kioskTheme === 'brand' ? (fontColor || c.text) : c.text;
              const btc = kioskTheme === 'brand' ? (buttonTextColor || '#000') : '#000';
              const sbg = kioskTheme === 'brand' ? (surfaceColor || c.bg3) : c.bg3;
              return (
                <div style={{ marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Kiosk preview</div>
                  <div style={{ background:bg, borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:tc, marginBottom:10 }}>
                      {vendor?.name || 'Your Stall'}
                    </div>
                    <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                      <div style={{ flex:1, background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>🔍 Search</div>
                      <div style={{ background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>🌐 EN</div>
                      <div style={{ background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>Category</div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      {[1,2].map(i => (
                        <div key={i} style={{ flex:1, background:tbg, borderRadius:8, padding:10 }}>
                          <div style={{ fontSize:11, color:tc, marginBottom:6 }}>Sample Item {i}</div>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:ac }}>AED {i * 12}.00</span>
                            <span style={{ width:20, height:20, borderRadius:'50%', background:ac, color:btc, fontSize:12, display:'flex', alignItems:'center', justifyContent:'center' }}>+</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" disabled={themeSaving} onClick={saveTheme}>
                {themeSaving ? '⏳ Saving...' : 'Save Appearance'}
              </button>
            </div>
          </div>

          {/* ── Payment Collection ── */}
          <div className="card" style={{ marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:14 }}>💰 Payment Collection</div>

            <div className="section-label" style={{ marginBottom:6 }}>Collection Mode</div>
            <select value={collectionMode} onChange={e => setCollectionMode(e.target.value)}
              style={{ marginBottom:12 }}>
              <option value="PLATFORM_DEFAULT">Platform Default</option>
              <option value="VENDOR_DIRECT">My Own Account (Direct)</option>
            </select>

            {collectionMode === 'VENDOR_DIRECT' && (
              <>
                <div className="section-label" style={{ marginBottom:6 }}>Payment Provider</div>
                <select value={paymentProvider} onChange={e => setPaymentProvider(e.target.value)}
                  style={{ marginBottom:10 }}>
                  <option value="TAP">Tap Payments</option>
                </select>

                <div className="section-label" style={{ marginBottom:6 }}>Account ID</div>
                <input type="text" placeholder="acct_xxxxxxxx"
                  value={paymentAccountId}
                  onChange={e => setPaymentAccountId(e.target.value)}
                  style={{ marginBottom:10 }} />
                <div style={{ padding:'10px 14px', background:'#1a2d3d', borderRadius:8, marginBottom:12, fontSize:12, color:'var(--text2)' }}>
                  💡 QuickServe currently processes payments through Tap Payments. Your
                  single-vendor orders settle to your own account. (Gateway routing
                  activates once your account is connected.)
                </div>
              </>
            )}

            {collectionMsg && <div style={{ fontSize:13, marginBottom:10 }}>{collectionMsg}</div>}

            <button className="btn btn-primary" disabled={collectionSaving} onClick={saveCollectionMode}>
              {collectionSaving ? '⏳ Saving...' : 'Save Payment Collection'}
            </button>
          </div>

          {/* ── Service Fee ── */}
          <div className="card" style={{ marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:16 }}>💰 Service Fee</div>
              {feeMsg && <span style={{ fontSize:13, color: feeMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{feeMsg}</span>}
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>Enable Service Fee</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>Charge a service fee on orders placed through your kiosk</div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={!!feeConfig.enabled}
                  onChange={e => setFeeConfig(p => ({ ...p, enabled: e.target.checked }))} />
                <span style={{ fontSize:13 }}>{feeConfig.enabled ? 'On' : 'Off'}</span>
              </label>
            </div>

            {feeConfig.enabled && (
              <>
                <div style={{ marginBottom:12 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Fee Type</div>
                  <select value={feeConfig.type} onChange={e => setFeeConfig(p => ({ ...p, type: e.target.value }))}
                    style={{ padding:'8px 12px', borderRadius:8, background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text)', fontSize:13 }}>
                    <option value="FIXED">Fixed Amount</option>
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="HYBRID">Hybrid (Fixed + Percentage)</option>
                  </select>
                </div>

                {(feeConfig.type === 'FIXED' || feeConfig.type === 'HYBRID') && (
                  <div style={{ marginBottom:12 }}>
                    <div className="section-label" style={{ marginBottom:6 }}>Fixed Amount (AED)</div>
                    <input type="number" min="0" step="0.01" value={feeConfig.fixedAmount || ''}
                      onChange={e => setFeeConfig(p => ({ ...p, fixedAmount: parseFloat(e.target.value) || 0 }))}
                      style={{ maxWidth:140 }} />
                  </div>
                )}

                {(feeConfig.type === 'PERCENTAGE' || feeConfig.type === 'HYBRID') && (
                  <div style={{ marginBottom:12 }}>
                    <div className="section-label" style={{ marginBottom:6 }}>Percentage (%)</div>
                    <input type="number" min="0" max="50" step="0.1" value={feeConfig.percentage || ''}
                      onChange={e => setFeeConfig(p => ({ ...p, percentage: parseFloat(e.target.value) || 0 }))}
                      style={{ maxWidth:140 }} />
                  </div>
                )}

                <div style={{ marginBottom:12 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Maximum Fee Cap (AED)</div>
                  <input type="number" min="0" step="0.01" value={feeConfig.maxCap || ''}
                    onChange={e => setFeeConfig(p => ({ ...p, maxCap: parseFloat(e.target.value) || 0 }))}
                    style={{ maxWidth:140 }} />
                  <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>Fee will never exceed this amount regardless of order size. Set 0 for no cap.</div>
                </div>

                <div style={{ marginBottom:12 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Minimum Order for Fee (AED)</div>
                  <input type="number" min="0" step="0.01" value={feeConfig.minOrderAmount || ''}
                    onChange={e => setFeeConfig(p => ({ ...p, minOrderAmount: parseFloat(e.target.value) || 0 }))}
                    style={{ maxWidth:140 }} />
                  <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>No fee charged on orders below this amount. Set 0 to always charge.</div>
                </div>

                <div style={{ marginBottom:12 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Customer-Facing Label</div>
                  <input type="text" value={feeConfig.label || 'Service Fee'}
                    onChange={e => setFeeConfig(p => ({ ...p, label: e.target.value }))}
                    placeholder="Service Fee"
                    style={{ maxWidth:260 }} />
                  <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>What customers see on their bill</div>
                </div>

                <div style={{ padding:'10px 14px', background:'var(--bg3)', borderRadius:8, marginBottom:16, fontSize:12, color:'var(--text2)' }}>
                  <strong style={{ color:'var(--text)' }}>Fee Split:</strong> For independent vendors, service fees go 100% to the platform.
                  <input type="hidden" value={100} onChange={() => setFeeConfig(p => ({ ...p, platformSharePercent: 100, mallSharePercent: 0 }))} />
                </div>

                {/* Preview calculator */}
                <div style={{ padding:'14px 16px', background:'var(--bg3)', borderRadius:10, border:'1px solid var(--border)' }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Preview Calculator</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <span style={{ fontSize:13, color:'var(--text2)' }}>On an AED</span>
                    <input type="number" min="0" value={feePreviewAmt}
                      onChange={e => setFeePreviewAmt(parseFloat(e.target.value) || 0)}
                      style={{ maxWidth:80, padding:'4px 8px' }} />
                    <span style={{ fontSize:13, color:'var(--text2)' }}>order:</span>
                  </div>
                  {(() => {
                    const preview = calculateServiceFee(feePreviewAmt, { ...feeConfig, platformSharePercent: 100, mallSharePercent: 0 });
                    return (
                      <div style={{ fontSize:14, color: preview.amount > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                        Customer pays: AED {preview.amount.toFixed(2)} {preview.amount === 0 ? '(no fee — below minimum or zero config)' : `${preview.label}`}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            <button className="btn btn-primary" style={{ marginTop:16 }} disabled={feeSaving} onClick={saveFeeConfig}>
              {feeSaving ? '⏳ Saving...' : 'Save Service Fee'}
            </button>
          </div>

          <button className="btn btn-primary"
            disabled={settingsSaving}
            onClick={async () => {
              if (!settingsForm.name?.trim()) return setSettingsMsg('❌ Name cannot be empty');
              const payload = {
                name:        settingsForm.name.trim(),
                emoji:       settingsForm.emoji       || vendor?.emoji       || '🍽',
                description: settingsForm.description || vendor?.description || '',
              };
              setSettingsSaving(true);
              try {
                const r = await api.patch('/vendors/me', payload);
                updateUser({ name: r.data.name, emoji: r.data.emoji, logo: r.data.logo });
                setSettingsMsg('✅ Saved');
              } catch (err) {
                setSettingsMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
              } finally {
                setSettingsSaving(false);
                setTimeout(() => setSettingsMsg(''), 3000);
              }
            }}>
            {settingsSaving ? '⏳ Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* ── Stock Tab ── */}
      {activeTab === 'stock' && <StockAlerts vendorId={vendor?.id} menu={menu} />}

      {/* ── Categories Tab ── */}
      {activeTab === 'categories' && (
        <div className="card">
          <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>📂 Menu Categories</div>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
            Add custom categories for your menu. These appear in the kiosk filter tabs.
          </div>

          {/* Add new category */}
          <div style={{ display:'flex', gap:8, marginBottom:20 }}>
            <input type="text" value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key==='Enter' && handleAddCategory()}
              placeholder="New category name e.g. Desserts, Combos..." />
            <button className="btn btn-primary" onClick={handleAddCategory} style={{ whiteSpace:'nowrap' }}>+ Add</button>
          </div>

          {/* Category list */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {categories.map((cat) => (
              <div key={cat} style={{
                display:'flex', alignItems:'center', gap:6,
                padding:'6px 14px', background:'var(--bg3)',
                borderRadius:20, border:'1px solid var(--border)',
              }}>
                <span style={{ fontSize:14 }}>{cat}</span>
                <button
                  onClick={() => handleRemoveCategory(cat)}
                  style={{ background:'none', border:'none', color:'var(--text3)', fontSize:14, cursor:'pointer', padding:0, lineHeight:1 }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment Methods Tab ── */}
      {activeTab === 'payments' && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:15 }}>💳 Payment Methods</div>
            {payMsg && <span style={{ fontSize:13, color: payMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{payMsg}</span>}
          </div>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
            Toggle which payment methods customers can use at your kiosk. Changes apply immediately.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {ALL_PAYMENT_METHODS.map((m) => (
              <div key={m.id} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'14px 16px', background:'var(--bg3)',
                borderRadius:10, border:`1px solid ${payConfig[m.id] ? 'var(--green)' : 'var(--border)'}`,
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:28 }}>{m.icon}</span>
                  <div>
                    <div style={{ fontWeight:600 }}>{m.label}</div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>{m.desc}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:12, color: payConfig[m.id] ? 'var(--green)' : 'var(--text3)' }}>
                    {payConfig[m.id] ? 'Enabled' : 'Disabled'}
                  </span>
                  <div className={`toggle${payConfig[m.id] ? ' on' : ''}`}
                    onClick={() => handleTogglePayment(m.id)}
                    style={{ cursor:'pointer' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Cash on QR orders */}
          {payConfig.CASH && (
            <div style={{ marginTop:16, padding:'14px 16px', background:'var(--bg3)', borderRadius:10, border:`1px solid ${payConfig.allowCashOnQR ? 'var(--accent)' : 'var(--border)'}` }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:24 }}>📱</span>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>Allow Cash on QR Orders</div>
                    <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>
                      When OFF (recommended): customers ordering via QR must pay by card or digital payment only.
                    </div>
                    <div style={{ fontSize:11, color:'var(--red)', marginTop:2 }}>
                      When ON: customer can select cash but must acknowledge going to counter first.
                    </div>
                  </div>
                </div>
                <div className={`toggle${payConfig.allowCashOnQR ? ' on' : ''}`}
                  onClick={() => handleTogglePayment('allowCashOnQR')}
                  style={{ cursor:'pointer', flexShrink:0 }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Menu Tab ── */}
      {activeTab === 'menu' && (
        <>
          {adding && (
            <div className="card" style={{ marginBottom:20 }}>
              <div style={{ fontWeight:700, marginBottom:16 }}>
                {editItem ? '✏️ Edit Menu Item' : 'New Menu Item'}
              </div>
              <div style={{ marginBottom:12 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Item Type</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="button"
                    className={`btn btn-sm ${!isBundleForm ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setForm({ ...form, itemType:'SIMPLE' })}>
                    🍽 Simple Item
                  </button>
                  <button type="button"
                    className={`btn btn-sm ${isBundleForm ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setForm({ ...form, itemType:'BUNDLE', components: form.components.length ? form.components : [] })}>
                    🍱 Bundle / Combo
                  </button>
                </div>
                {isBundleForm && (
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>
                    e.g. Pizza Hut "WOW Triple" — 3 independently customizable pizzas in one cart line.
                  </div>
                )}
              </div>

              <div className="grid2" style={{ gap:12, marginBottom:12 }}>
                <div>
                  <div className="section-label" style={{ marginBottom:6 }}>Item Name *</div>
                  <input type="text" value={form.name} placeholder={isBundleForm ? 'e.g. WOW Triple' : 'e.g. Spicy Wrap'}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom:6 }}>Price (AED) *</div>
                  <input type="number" value={form.price} placeholder="0"
                    onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom:6 }}>Category</div>
                  <select value={form.category || categories[0]} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom:6 }}>Emoji Icon</div>
                  <input type="text" value={form.emoji} placeholder="🍽"
                    onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
                </div>
              </div>

              <div style={{ marginBottom:12 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Description</div>
                <input type="text" value={form.description} placeholder="Short description"
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              {/* Image URL input */}
              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Image URL (optional)</div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="text" value={form.imageUrl} placeholder="https://example.com/image.jpg"
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
                  {form.imageUrl && (
                    <img src={form.imageUrl} alt="preview"
                      onError={(e) => e.target.style.display='none'}
                      style={{ width:48, height:48, borderRadius:8, objectFit:'cover', border:'1px solid var(--border)', flexShrink:0 }} />
                  )}
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                  Paste any image URL. Tip: use imgur.com or imgbb.com to host small images free.
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Dietary tags</div>
                <TagPicker options={DIETARY_TAGS} value={form.dietary || []} onChange={(dietary) => setForm({ ...form, dietary })} />
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                  Customers who pick a dietary preference only see items that carry a matching tag.
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Allergens</div>
                <TagPicker options={ALLERGEN_TAGS} value={form.allergens || []} onChange={(allergens) => setForm({ ...form, allergens })} />
              </div>

              {isBundleForm && (
                <div style={{ marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:8 }}>Bundle Components</div>
                  <BundleBuilder
                    components={form.components}
                    onChange={(components) => setForm({ ...form, components })}
                  />
                </div>
              )}

              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-primary" onClick={editItem ? handleUpdate : handleAdd}>
                  {editItem ? 'Save Changes' : 'Add Item'}
                </button>
                <button className="btn btn-ghost" onClick={() => { setAdding(false); setEditItem(null); setForm(EMPTY_FORM); }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:700 }}>Menu Items ({menu.length})</span>
              <span style={{ fontSize:13, color:'var(--text2)' }}>
                {menu.filter((i) => i.isActive).length} active ·{' '}
                {menu.filter((i) => i.posSource==='pos_sync').length} from POS ·{' '}
                {menu.filter((i) => !i.isActive).length} hidden
              </span>
            </div>

            {menu.map((item) => (
              <div key={item.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  {/* Show image if available, otherwise emoji */}
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name}
                      onError={(e) => { e.target.style.display='none'; }}
                      style={{ width:48, height:48, borderRadius:8, objectFit:'cover', border:'1px solid var(--border)' }} />
                  ) : (
                    <span style={{ fontSize:28 }}>{item.emoji}</span>
                  )}
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:6 }}>
                      {item.name}
                      {item.itemType==='BUNDLE' && (
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:10, background:'#2a1a3d', color:'#7F77DD' }}>🍱 Bundle</span>
                      )}
                      {item.posSource==='pos_sync' && (
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:10, background:'#1a2d3d', color:'var(--blue)' }}>POS</span>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>
                      {item.category} · AED {item.price} · Stock: {item.stock}
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span className={`badge ${item.isActive ? 'badge-green' : 'badge-red'}`}>
                    {item.isActive ? 'Live' : 'Hidden'}
                  </span>
                  <div className={`toggle${item.isActive ? ' on' : ''}`} onClick={() => handleToggle(item.id)} style={{ cursor:'pointer' }} />
                  <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(item)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>Remove</button>
                </div>
              </div>
            ))}

            {menu.length === 0 && (
              <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>
                No menu items yet. Add manually or connect your POS to import.
              </div>
            )}
          </div>
        </>
      )}
      {/* Menu importer modal */}
      {showImporter && (
        <MenuImportWizard
          onClose={() => setShowImporter(false)}
          onImported={() => { setShowImporter(false); loadMenu(); }}
        />
      )}
    </div>
  );
}
